import { useEffect, useRef } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { useGuestIdentificationDialogContext } from './GuestIdentificationDialog.context';

/**
 * Defers input focus until after the dialog/drawer animation completes.
 *
 * iOS Safari/Chrome doesn't trigger native keyboard scroll behavior for synchronous
 * programmatic focus. Deferring the focus with a delay lets iOS treat it more like
 * a user-initiated focus and correctly scroll the input into view above the keyboard.
 *
 * Re-triggers focus whenever the dialog transitions to open, or the step changes
 * to a new input (e.g. email → otp).
 */
export const useDeferredInputFocus = <T extends HTMLElement = HTMLInputElement>() => {
  const ref = useRef<T>(null);
  const { isOpen, step } = useGuestIdentificationDialogContext();

  useEffect(() => {
    if (!isOpen) return;

    const timer = withWindow(
      global =>
        global.setTimeout(() => {
          ref.current?.focus();
        }, 300),
      undefined,
    );
    return () => withWindow(global => global.clearTimeout(timer));
  }, [isOpen, step]);

  return ref;
};
